import React, { useCallback, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import EditOutlined from '@mui/icons-material/EditOutlined';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import ScheduleOutlined from '@mui/icons-material/ScheduleOutlined';
import Colors from '../../library/Colors';
import { surface, tone } from '../timeclock/tcFormat';
import { addDays, DEPARTMENTS, departmentLabel, hours, locale, parseDate, teamError, teamPost } from './teamFormat';

const FLAGS = ['is_manager', 'sees_all', 'edit_allowance', 'plan_duty', 'manage_staff', 'notify_requests', 'notify_sick'];
const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];

/** Right of a colleague: short title and what it allows. */
const flagText = (t, f) => ({
    is_manager: [t('team_flag_manager', 'Management'), t('team_flag_manager_hint', 'Approves requests; own leave is approved at once')],
    sees_all: [t('team_flag_sees_all', 'Sees everything'), t('team_flag_sees_all_hint', 'Kind, reason and sickness of everyone')],
    edit_allowance: [t('team_flag_allowance', 'Leave right'), t('team_flag_allowance_hint', 'Enters the leave right per year')],
    plan_duty: [t('team_flag_duty', 'Duty weeks'), t('team_flag_duty_hint', 'Plans the weekend duty')],
    manage_staff: [t('team_flag_staff', 'Staff'), t('team_flag_staff_hint', 'Colleagues, rights and rosters')],
    notify_requests: [t('team_flag_notify_requests', 'Mail on requests'), t('team_flag_notify_requests_hint', 'Only when no department mailbox is set')],
    notify_sick: [t('team_flag_notify_sick', 'Mail on sickness'), t('team_flag_notify_sick_hint', 'Only when no sickness mailbox is set')],
}[f] || [f, '']);

const emptyStaff = { id: 0, name: '', email: '', department: 'SUPPORT', coverage: 'SUPPORT', discord_id: '', sort_order: 200, is_active: true,
    is_manager: false, sees_all: false, edit_allowance: false, plan_duty: false, notify_requests: false, notify_sick: false, manage_staff: false };

/** Minutes between two "HH:MM" times. */
const minutes = (a, b) => {
    if (!/^\d\d:\d\d$/.test(a || '') || !/^\d\d:\d\d$/.test(b || '')) return 0;
    const [ah, am] = a.split(':').map(Number);
    const [bh, bm] = b.split(':').map(Number);
    return (bh * 60 + bm) - (ah * 60 + am);
};

const initials = (name) => (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

/** Small uppercase heading that opens a group of fields in a dialog. */
const Section = ({ label, children, faint }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: faint }}>{label}</Typography>
        {children}
    </Box>
);

const DialogHead = ({ title, sub }) => (
    <DialogTitle sx={{ pb: 1.5 }}>
        <Typography component="span" sx={{ display: 'block', fontSize: 18, fontWeight: 700, lineHeight: 1.3 }}>{title}</Typography>
        {sub && <Typography component="span" sx={{ display: 'block', fontSize: 13, color: 'text.secondary', mt: 0.25 }}>{sub}</Typography>}
    </DialogTitle>
);

/**
 * Beheer -> Personeel: colleagues, their rights and rosters. For whoever has
 * manage_staff. A colleague who leaves is set inactive (history stays).
 */
const TeamStaffPanel = ({ t, lang, theme, token, me, notify, onChanged }) => {
    const [list, setList] = useState(null);
    const [error, setError] = useState('');
    const [again, setAgain] = useState(0);
    const [showInactive, setShowInactive] = useState(false);
    const [edit, setEdit] = useState(null);
    const [roster, setRoster] = useState(null);

    useEffect(() => {
        let alive = true;
        teamPost('staffAdmin', token).then((response) => {
            if (!alive) return;
            if (response?.success) { setList(response.data || []); setError(''); } else setError(teamError(t, response));
        });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, again]);

    const reload = useCallback(() => { setAgain((k) => k + 1); if (onChanged) onChanged(); }, [onChanged]);

    const s = surface(theme);
    const loc = locale(lang);
    const active = (list || []).filter((p) => p.is_active).length;
    const inactive = (list || []).length - active;
    const shown = (list || []).filter((p) => showInactive || p.is_active);
    const th = { fontSize: 11.5, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: s.faint, textAlign: 'left', px: 2, py: 1, whiteSpace: 'nowrap' };
    const td = { fontSize: 13.5, px: 2, py: 1.25, borderTop: s.line, verticalAlign: 'middle' };
    const chip = (text, color) => (
        <Box component="span" sx={{ fontSize: 11.5, fontWeight: 600, px: 0.9, py: 0.2, borderRadius: '6px', whiteSpace: 'nowrap',
            backgroundColor: color ? color + '1A' : s.hover, color: color || 'text.secondary' }}>{text}</Box>
    );

    return (
        <Box sx={{ ...s.card, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', px: 2.25, py: 1.75, borderBottom: s.line }}>
                <Box sx={{ mr: 'auto' }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1.3 }}>{t('team_staff_title', 'Staff')}</Typography>
                    {list && (
                        <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                            {active} {t('team_staff_word_active', 'active')}{inactive > 0 && ' · ' + inactive + ' ' + t('team_staff_word_inactive', 'left')}
                        </Typography>
                    )}
                </Box>
                {inactive > 0 && (
                    <FormControlLabel sx={{ mr: 0 }} control={<Switch size="small" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />}
                        label={<Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>{t('team_staff_show_inactive', 'Show inactive')}</Typography>} />
                )}
                <Button variant="contained" disableElevation startIcon={<AddIcon />} onClick={() => setEdit({ ...emptyStaff })}
                    sx={{ textTransform: 'none', height: 38 }}>
                    {t('team_staff_add', 'New colleague')}
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
            {list && (
                <Box sx={{ overflowX: 'auto' }}>
                    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
                        <thead>
                            <tr>
                                <Box component="th" sx={th}>{t('team_col_name', 'Colleague')}</Box>
                                <Box component="th" sx={th}>{t('team_staff_department', 'Department')}</Box>
                                <Box component="th" sx={th}>{t('team_staff_login', 'MySoftTouch login')}</Box>
                                <Box component="th" sx={th}>{t('team_staff_roster', 'Roster')}</Box>
                                <Box component="th" sx={th}>{t('team_staff_rights', 'Rights')}</Box>
                                <Box component="th" sx={{ ...th, width: 48 }} />
                            </tr>
                        </thead>
                        <tbody>
                            {shown.map((p) => (
                                <Box component="tr" key={p.id} sx={{ opacity: p.is_active ? 1 : 0.55, '&:hover': { backgroundColor: s.hover } }}>
                                    <Box component="td" sx={td}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                            <Box sx={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontSize: 12, fontWeight: 700, color: '#fff', backgroundColor: p.is_active ? Colors.sft_blue : tone.muted }}>
                                                {initials(p.name)}
                                            </Box>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography sx={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.3 }}>
                                                    {p.name}
                                                    {p.id === me.staff.id && <Box component="span" sx={{ color: s.faint, fontWeight: 400 }}> ({t('team_staff_you', 'you')})</Box>}
                                                </Typography>
                                                <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.3 }}>
                                                    {p.is_active ? (p.email || '—') : t('team_staff_inactive', 'inactive')}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Box component="td" sx={td}>
                                        <Typography sx={{ fontSize: 13.5, lineHeight: 1.3 }}>{departmentLabel(t, p.department)}</Typography>
                                        {p.coverage && p.coverage !== p.department && (
                                            <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.3 }}>
                                                {t('team_staff_counts_for', 'counts for')} {departmentLabel(t, p.coverage).toLowerCase()}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box component="td" sx={td}>
                                        {p.login ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: tone.ok }}>
                                                <CheckCircleOutline sx={{ fontSize: 17 }} />
                                                <Typography sx={{ fontSize: 13.5 }}>{t('team_staff_linked', 'linked')}</Typography>
                                                {p.sft_user_id && <Typography sx={{ fontSize: 12, color: s.faint }}>· {p.sft_user_id}</Typography>}
                                            </Box>
                                        ) : (
                                            <Tooltip title={p.email ? t('team_staff_no_login', 'no login found for') + ' ' + p.email : ''}>
                                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, color: tone.warn }}>
                                                    <ErrorOutline sx={{ fontSize: 17 }} />
                                                    <Typography sx={{ fontSize: 13.5 }}>{p.email ? t('team_staff_not_linked', 'not linked') : t('team_staff_no_email', 'no e-mail')}</Typography>
                                                </Box>
                                            </Tooltip>
                                        )}
                                    </Box>
                                    <Box component="td" sx={td}>
                                        <Button size="small" startIcon={<ScheduleOutlined sx={{ fontSize: '17px !important' }} />} onClick={() => setRoster({ person: p })}
                                            sx={{ textTransform: 'none', fontWeight: 600, ml: -0.75, color: p.rosters.length ? 'text.primary' : tone.warn }}>
                                            {p.rosters.length ? hours(p.rosters[0].week_hours, lang) + ' / ' + t('team_week_word', 'week') : t('team_staff_no_roster', 'set up')}
                                        </Button>
                                    </Box>
                                    <Box component="td" sx={td}>
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                            {p.is_manager && chip(t('team_chip_manager', 'management'), Colors.sft_blue)}
                                            {p.sees_all && !p.is_manager && chip(t('team_chip_sees_all', 'sees all'))}
                                            {p.edit_allowance && chip(t('team_chip_allowance', 'leave right'))}
                                            {p.plan_duty && chip(t('team_chip_duty', 'duty weeks'))}
                                            {p.manage_staff && chip(t('team_chip_staff', 'staff'))}
                                            {!p.is_manager && !p.sees_all && !p.edit_allowance && !p.plan_duty && !p.manage_staff && <Box component="span" sx={{ color: s.faint }}>—</Box>}
                                        </Box>
                                    </Box>
                                    <Box component="td" sx={{ ...td, textAlign: 'right', pr: 1 }}>
                                        <Tooltip title={t('team_staff_edit', 'Edit')}>
                                            <IconButton size="small" onClick={() => setEdit({ ...p, coverage: p.coverage || '', email: p.email || '', discord_id: p.discord_id || '' })}>
                                                <EditOutlined sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            ))}
                        </tbody>
                    </Box>
                </Box>
            )}
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', px: 2.25, py: 1.5, borderTop: s.line }}>
                {t('team_staff_note', 'The login is found by e-mail on the SoftTouch Vlaanderen database; without it the colleague does not see the planner. Someone who leaves is set inactive: history and balances stay, the name disappears from the calendar. Every change is logged.')}
            </Typography>

            <StaffDialog open={!!edit} value={edit} t={t} theme={theme} token={token} me={me}
                onClose={() => setEdit(null)}
                onSaved={(res, wasNew, person) => {
                    setEdit(null);
                    notify(res.linked ? t('team_staff_saved_linked', 'Saved; the login is linked.') : t('team_staff_saved', 'Saved.'), true);
                    reload();
                    if (wasNew) setRoster({ person: { ...person, id: res.id, rosters: [] } });
                }} />
            <RosterDialog open={!!roster} person={roster?.person} t={t} lang={lang} loc={loc} theme={theme} token={token}
                onClose={() => setRoster(null)}
                onSaved={() => { setRoster(null); notify(t('team_saved', 'Saved'), true); reload(); }} />
        </Box>
    );
};

const StaffDialog = ({ open, value, t, theme, token, me, onClose, onSaved }) => {
    const [v, setV] = useState(emptyStaff);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    useEffect(() => { if (open && value) { setV(value); setError(''); } }, [open, value]);
    const set = (k) => (e) => setV({ ...v, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
    const toggle = (k) => setV({ ...v, [k]: !v[k] });
    const self = !!v.id && v.id === me.staff.id;
    const s = surface(theme);

    const save = () => {
        setBusy(true);
        setError('');
        teamPost('saveStaff', token, { staff: v }).then((response) => {
            setBusy(false);
            if (response?.success) onSaved(response.data, !v.id, v);
            else setError(teamError(t, response));
        });
    };

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogHead title={v.id ? (value?.name || v.name) : t('team_staff_add', 'New colleague')}
                sub={v.id ? departmentLabel(t, value?.department) : t('team_staff_new_sub', 'After saving you set up the roster.')} />
            <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 3, py: 2.5 }}>
                <Section label={t('team_staff_section_details', 'Details')} faint={s.faint}>
                    <TextField size="small" fullWidth autoFocus={!v.id} label={t('team_staff_name', 'Name (as in the weekly schedule)')} value={v.name} onChange={set('name')} />
                    <TextField size="small" fullWidth label={t('team_staff_email', 'E-mail = MySoftTouch login')} value={v.email} onChange={set('email')}
                        helperText={t('team_staff_email_help', 'The MySoftTouch login with this address is linked automatically.')} />
                    <TextField size="small" fullWidth label={t('team_staff_discord', 'Discord ID')} value={v.discord_id} onChange={set('discord_id')}
                        helperText={t('team_staff_discord_help', 'Digits only; for @mentions in the Discord messages.')} />
                </Section>

                <Section label={t('team_staff_section_team', 'Place in the team')} faint={s.faint}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 96px' }, gap: 1.5 }}>
                        <TextField select size="small" label={t('team_staff_department', 'Department')} value={v.department} onChange={set('department')}>
                            {DEPARTMENTS.map((d) => <MenuItem key={d} value={d}>{departmentLabel(t, d)}</MenuItem>)}
                        </TextField>
                        <TextField select size="small" label={t('team_staff_coverage', 'Counts for')} value={v.coverage} onChange={set('coverage')}>
                            <MenuItem value="">{t('team_staff_cov_none', 'No coverage rule')}</MenuItem>
                            <MenuItem value="DEV">{departmentLabel(t, 'DEV')}</MenuItem>
                            <MenuItem value="SUPPORT">{departmentLabel(t, 'SUPPORT')}</MenuItem>
                        </TextField>
                        <TextField size="small" type="number" label={t('team_staff_order', 'Order')} value={v.sort_order} onChange={set('sort_order')} />
                    </Box>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: -0.5 }}>
                        {t('team_staff_coverage_help', 'Development: at least one present. Support: at most two away. Order: lower comes first in the lists.')}
                    </Typography>
                </Section>

                <Section label={t('team_staff_rights', 'Rights')} faint={s.faint}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                        {FLAGS.map((f) => {
                            const [title, hint] = flagText(t, f);
                            const locked = self && f === 'manage_staff';
                            return (
                                <Box key={f} onClick={() => { if (!locked) toggle(f); }}
                                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, p: 1, pr: 1.5, borderRadius: '8px', cursor: locked ? 'default' : 'pointer',
                                        border: '1px solid ' + (v[f] ? Colors.sft_blue : (s.dark ? 'rgba(255,255,255,0.10)' : Colors.light_accent_darker)),
                                        backgroundColor: v[f] ? Colors.sft_blue + '12' : 'transparent', opacity: locked ? 0.6 : 1,
                                        transition: 'border-color .15s, background-color .15s' }}>
                                    <Checkbox size="small" checked={!!v[f]} disabled={locked} sx={{ p: 0.5 }}
                                        onClick={(e) => e.stopPropagation()} onChange={() => toggle(f)} />
                                    <Box sx={{ pt: 0.4, minWidth: 0 }}>
                                        <Typography sx={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.3 }}>{title}</Typography>
                                        <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.35 }}>{hint}</Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </Section>

                {v.id > 0 && (
                    <Section label={t('team_staff_section_status', 'Status')} faint={s.faint}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Switch checked={!!v.is_active} disabled={self} onChange={set('is_active')} />
                            <Box>
                                <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{t('team_staff_active', 'Active (in service)')}</Typography>
                                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                                    {t('team_staff_active_help', 'Leaving: history and balances stay, the name disappears from the calendar and the planner.')}
                                </Typography>
                            </Box>
                        </Box>
                    </Section>
                )}
                {self && <Alert severity="info" sx={{ py: 0 }}>{t('team_staff_self_note', 'You cannot change your own staff right or status.')}</Alert>}
                {error && <Alert severity="error">{error}</Alert>}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1.75 }}>
                <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>{t('cancel', 'Cancel')}</Button>
                <Button variant="contained" disableElevation onClick={save} disabled={busy || !v.name.trim()} sx={{ textTransform: 'none', minWidth: 110 }}>
                    {busy ? t('tc_busy', 'One moment…') : t('team_save', 'Save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const fullTime = () => WEEKDAYS.map((wd) => ({ weekday: wd, works: wd <= 5, start: '08:30', end: wd === 5 ? '15:30' : '17:30', break_min: 36, recup_h: 0.4 }));

const RosterDialog = ({ open, person, t, lang, loc, theme, token, onClose, onSaved }) => {
    const [from, setFrom] = useState('');
    const [days, setDays] = useState(fullTime());
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const load = (r) => {
        const map = {};
        (r?.days || []).forEach((d) => { map[d.weekday] = d; });
        setDays(WEEKDAYS.map((wd) => (map[wd]
            ? { weekday: wd, works: true, start: map[wd].start, end: map[wd].end, break_min: map[wd].break_min, recup_h: map[wd].recup_h }
            : { weekday: wd, works: false, start: '08:30', end: '17:30', break_min: 36, recup_h: 0.4 })));
    };

    useEffect(() => {
        if (!open || !person) return;
        setError('');
        const current = person.rosters && person.rosters[0];
        setFrom(current ? current.valid_from : new Date().toISOString().substring(0, 10));
        if (current) load(current); else setDays(fullTime());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, person]);

    const s = surface(theme);
    const dayHours = (d) => Math.max(0, minutes(d.start, d.end) - (Number(d.break_min) || 0)) / 60;
    const total = days.filter((d) => d.works).reduce((a, d) => a + dayHours(d), 0);
    const change = (i, k, value) => setDays(days.map((d, j) => (j === i ? { ...d, [k]: value } : d)));
    const dayName = (wd) => parseDate(addDays('2026-01-05', wd - 1)).toLocaleDateString(loc, { weekday: 'long' });
    const head = { fontSize: 11.5, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: s.faint };

    const save = () => {
        setBusy(true);
        setError('');
        teamPost('saveRoster', token, { staff_id: person.id, valid_from: from, days }).then((response) => {
            setBusy(false);
            if (response?.success) onSaved(); else setError(teamError(t, response));
        });
    };
    const remove = (validFrom) => {
        if (!window.confirm(t('team_roster_delete_confirm', 'Remove this roster?') + ' ' + validFrom)) return;
        teamPost('deleteRoster', token, { staff_id: person.id, valid_from: validFrom }).then((response) => {
            if (response?.success) onSaved(); else setError(teamError(t, response));
        });
    };

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
            <DialogHead title={t('team_roster_title', 'Roster') + ' · ' + (person?.name || '')}
                sub={t('team_total', 'Total') + ': ' + hours(total, lang) + ' / ' + t('team_week_word', 'week')} />
            <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 3, py: 2.5 }}>
                {person?.rosters?.length > 0 && (
                    <Section label={t('team_roster_existing', 'Rosters')} faint={s.faint}>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {person.rosters.map((r) => (
                                <Box key={r.valid_from} sx={{ display: 'inline-flex', alignItems: 'center', borderRadius: '8px', pl: 0.5,
                                    border: '1px solid ' + (r.valid_from === from ? Colors.sft_blue : (s.dark ? 'rgba(255,255,255,0.10)' : Colors.light_accent_darker)) }}>
                                    <Button size="small" onClick={() => { setFrom(r.valid_from); load(r); }} sx={{ textTransform: 'none', color: 'text.primary' }}>
                                        {t('team_roster_from', 'from')} {r.valid_from} · {hours(r.week_hours, lang)}
                                    </Button>
                                    {person.rosters.length > 1 && (
                                        <Tooltip title={t('team_roster_delete_confirm', 'Remove this roster?')}>
                                            <Button size="small" color="error" onClick={() => remove(r.valid_from)} sx={{ minWidth: 0, px: 1 }}>×</Button>
                                        </Tooltip>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    </Section>
                )}

                <Section label={t('team_roster_valid_from', 'Valid from')} faint={s.faint}>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                        <TextField type="date" size="small" value={from} onChange={(e) => setFrom(e.target.value)} sx={{ width: 180 }} />
                        <Button size="small" variant="outlined" onClick={() => setDays(fullTime())} sx={{ textTransform: 'none', height: 40 }}>
                            {t('team_roster_full_time', 'Full time (40 h, 0,4 recup)')}
                        </Button>
                    </Box>
                </Section>

                <Section label={t('team_roster_week', 'Week')} faint={s.faint}>
                    <Box sx={{ overflowX: 'auto' }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '150px 110px 110px 100px 100px 1fr', columnGap: 1.5, rowGap: 1, alignItems: 'center', minWidth: 700 }}>
                            <Box sx={head}>{t('team_roster_day', 'Day')}</Box>
                            <Box sx={head}>{t('team_roster_start', 'Start')}</Box>
                            <Box sx={head}>{t('team_roster_end', 'End')}</Box>
                            <Box sx={head}>{t('team_roster_break', 'Break (min)')}</Box>
                            <Box sx={head}>{t('team_roster_recup', 'Recup (h)')}</Box>
                            <Box sx={{ ...head, textAlign: 'right' }}>{t('team_hours', 'Hours')}</Box>
                            {days.map((d, i) => (
                                <React.Fragment key={d.weekday}>
                                    <FormControlLabel sx={{ m: 0 }} control={<Checkbox size="small" checked={d.works} onChange={(e) => change(i, 'works', e.target.checked)} />}
                                        label={<Typography sx={{ fontSize: 13.5, textTransform: 'capitalize', color: d.works ? 'text.primary' : s.faint }}>{dayName(d.weekday)}</Typography>} />
                                    <TextField size="small" type="time" disabled={!d.works} value={d.start} onChange={(e) => change(i, 'start', e.target.value)} />
                                    <TextField size="small" type="time" disabled={!d.works} value={d.end} onChange={(e) => change(i, 'end', e.target.value)} />
                                    <TextField size="small" type="number" disabled={!d.works} value={d.break_min} onChange={(e) => change(i, 'break_min', e.target.value)} />
                                    <TextField size="small" type="number" disabled={!d.works} value={d.recup_h} slotProps={{ htmlInput: { step: 0.1 } }} onChange={(e) => change(i, 'recup_h', e.target.value)} />
                                    <Box sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 13.5, fontWeight: 600, color: d.works ? 'text.primary' : s.faint }}>
                                        {d.works ? hours(dayHours(d), lang) : '—'}
                                    </Box>
                                </React.Fragment>
                            ))}
                        </Box>
                    </Box>
                </Section>

                <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                    {t('team_roster_note', 'A day off costs the hours of that day. A new contract: pick the date it starts; the previous roster keeps applying before it. Changing a roster that already applies changes the balances of that period too.')}
                </Typography>
                {error && <Alert severity="error">{error}</Alert>}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1.75 }}>
                <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>{t('cancel', 'Cancel')}</Button>
                <Button variant="contained" disableElevation onClick={save} disabled={busy || !from} sx={{ textTransform: 'none', minWidth: 110 }}>
                    {busy ? t('tc_busy', 'One moment…') : t('team_save', 'Save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TeamStaffPanel;
